import { useMemo } from 'react';
import Hint from '../../../../../components/common/Hint';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import ToolCard from '../../components/common/ToolCard';
import { availableOutputFormats } from './imageOutputFormats';
import { outputFormatIcon } from './outputFormatIcons';

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/**
 * The output-format card every image tool shares. Offers only what this browser
 * can actually write (AVIF is missing on some), and says so when JPG is picked
 * for output that may be see-through.
 */
export default function OutputFormatPicker({
    value,
    onChange,
    formats = ['original', 'jpg', 'png', 'webp', 'avif'],
    disabled = false,
    /** True when the tool's output can carry transparency JPG would lose. */
    transparentOutput = false,
    /**
     * Render as a section of a panel the page already draws (title + control, no
     * card of its own), for tools whose settings column is one white panel.
     */
    bare = false,
    /**
     * When set, only these formats can be picked; the rest stay visible but
     * disabled. For a setting that dictates the format (a size target needs JPG).
     */
    enabledIds = null,
    /** Tooltip for the formats `enabledIds` turns off — say why they are unavailable. */
    lockedHint,
}) {
    const { t } = useTranslation();
    const key = formats.join(',');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const options = useMemo(() => availableOutputFormats(formats), [key]);

    const body = (
        <>
            <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                {options.map((id) => {
                    const Icon = outputFormatIcon(id);
                    const label = t(`toolPage.outputFormat.formats.${id}`);
                    const locked = Boolean(enabledIds) && !enabledIds.includes(id);
                    const button = (
                        <button
                            type="button"
                            onClick={() => onChange(id)}
                            disabled={disabled || locked}
                            className={segBtnClass(value === id)}
                        >
                            <Icon size={20} stroke={1.8} />
                            {label}
                        </button>
                    );
                    // A disabled button swallows hover, so the reason hangs off a wrapper.
                    return locked && lockedHint ? (
                        <Hint label={lockedHint} key={id}>
                            <span className="flex flex-1 [&>button]:w-full">{button}</span>
                        </Hint>
                    ) : (
                        <Hint label={label} key={id}>{button}</Hint>
                    );
                })}
            </div>
            {transparentOutput && value === 'jpg' ? (
                <p className="mt-2 text-[11.5px] leading-relaxed text-amber-600 dark:text-amber-400">
                    {t('toolPage.outputFormat.jpgNoTransparency')}
                </p>
            ) : null}
        </>
    );

    if (bare) {
        return (
            <section>
                <p className="mb-2.5 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                    {t('toolPage.outputFormat.title')}
                </p>
                {body}
            </section>
        );
    }

    return <ToolCard title={t('toolPage.outputFormat.title')}>{body}</ToolCard>;
}
